import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Ban, Plus, Search, Upload, Download, FileSpreadsheet, FileText, Printer,
  Trash2, Pencil, Eye, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle,
  ShieldOff, ChevronLeft, ChevronRight, Ban as BanIcon, DollarSign,
  Activity, TrendingUp, Building2,
} from "lucide-react";
import CurrencyConverterPanel from "@/components/CurrencyConverterPanel";
import CompanyPrintPanel from "@/components/CompanyPrintPanel";
import UploadPanel from "@/components/UploadPanel";
import PrintPanel from "@/components/PrintPanel";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250];

const SortIcon = ({ col, sortBy, sortDir }) => (
  sortBy !== col ? <ArrowUpDown className="w-3 h-3 inline text-slate-400 ml-1" /> :
  sortDir === "asc" ? <ArrowUp className="w-3 h-3 inline text-indigo-600 ml-1" /> :
  <ArrowDown className="w-3 h-3 inline text-indigo-600 ml-1" />
);

const ProcessingTeamPanel = ({ user }) => {
  const isAdmin = user?.role === "admin";
  const subPanels = user?.processing_team_sub_panels || {};
  // DEFAULT-DENY: sub-panel access requires the key to be explicitly `true`.
  const canSeeBanned = isAdmin || subPanels.banned_products === true;
  const canSeeCurrency = isAdmin || subPanels.currency_converter === true;
  const canSeeCompanyPrint = isAdmin || subPanels.company_print === true;
  const canSeeUpload = isAdmin || subPanels.upload_panel === true;
  const canSeePrint = isAdmin || subPanels.print_panel === true;
  // Order Register lives ONLY as its own top-level panel now (sidebar →
  // Order Register); the duplicate sub-tab here was removed.
  const hasAccess = canSeeBanned || canSeeCurrency || canSeeCompanyPrint || canSeeUpload || canSeePrint;

  // Pick a default tab based on which sub-panel the user can see
  const defaultTab = canSeeUpload ? "upload"
                    : canSeeBanned ? "banned"
                    : canSeeCurrency ? "currency"
                    : canSeeCompanyPrint ? "company_print"
                    : "print";
  const [activeTab, setActiveTab] = useState(defaultTab);

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="processing-access-denied">
        <Card className="max-w-md border-red-200 bg-red-50/50">
          <CardContent className="pt-8 pb-8 text-center">
            <ShieldOff className="w-10 h-10 mx-auto mb-3 text-red-500" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h3>
            <p className="text-sm text-red-600">Contact your administrator for Processing Team access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50" data-testid="processing-team-panel">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="border-b bg-white px-4 pt-3">
          <TabsList className="bg-slate-100" data-testid="processing-team-tabs">
            {canSeeUpload && (
              <TabsTrigger value="upload" className="data-[state=active]:bg-white data-[state=active]:text-indigo-700"
                           data-testid="tab-upload-panel">
                <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Panel
              </TabsTrigger>
            )}
            {canSeeBanned && (
              <TabsTrigger value="banned" className="data-[state=active]:bg-white data-[state=active]:text-indigo-700"
                           data-testid="tab-banned-products">
                <BanIcon className="w-3.5 h-3.5 mr-1.5" /> Banned Product Info
              </TabsTrigger>
            )}
            {canSeeCurrency && (
              <TabsTrigger value="currency" className="data-[state=active]:bg-white data-[state=active]:text-indigo-700"
                           data-testid="tab-currency-converter">
                <DollarSign className="w-3.5 h-3.5 mr-1.5" /> Currency Converter
              </TabsTrigger>
            )}
            {canSeeCompanyPrint && (
              <TabsTrigger value="company_print" className="data-[state=active]:bg-white data-[state=active]:text-indigo-700"
                           data-testid="tab-company-print">
                <Building2 className="w-3.5 h-3.5 mr-1.5" /> Company Wise Print
              </TabsTrigger>
            )}
            {canSeePrint && (
              <TabsTrigger value="print" className="data-[state=active]:bg-white data-[state=active]:text-indigo-700"
                           data-testid="tab-print-panel">
                <Printer className="w-3.5 h-3.5 mr-1.5" /> Print Panel
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        {canSeeUpload && (
          <TabsContent value="upload" className="mt-0">
            <UploadPanel user={user} />
          </TabsContent>
        )}
        {canSeeBanned && (
          <TabsContent value="banned" className="mt-0">
            <BannedProductsTab user={user} />
          </TabsContent>
        )}
        {canSeeCurrency && (
          <TabsContent value="currency" className="mt-0">
            <CurrencyConverterPanel user={user} />
          </TabsContent>
        )}
        {canSeeCompanyPrint && (
          <TabsContent value="company_print" className="mt-0">
            <CompanyPrintPanel user={user} />
          </TabsContent>
        )}
        {canSeePrint && (
          <TabsContent value="print" className="mt-0">
            <PrintPanel user={user} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

/* ---------------------- Banned Products Tab ---------------------- */

const BannedProductsTab = ({ user }) => {
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("product_name");
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [drugClasses, setDrugClasses] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ product_name: "", generic_name: "", drug_class: "" });

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { setPage(1); }, [debouncedQ]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/banned-products`, {
        headers: getAuthHeader(),
        params: { q: debouncedQ, page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir },
      });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      if (!debouncedQ) setTotalAll(res.data.total || 0);
    } catch { toast.error("Failed to load banned products"); }
    finally { setLoading(false); }
  }, [debouncedQ, page, pageSize, sortBy, sortDir]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/banned-products/drug-classes`, { headers: getAuthHeader() })
      .then(r => setDrugClasses(r.data.classes || []))
      .catch(() => {});
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const changeSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const openCreate = () => { setEditing(null); setForm({ product_name: "", generic_name: "", drug_class: "" }); setEditOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ product_name: r.product_name, generic_name: r.generic_name, drug_class: r.drug_class }); setEditOpen(true); };
  const openView = (r) => { setViewRow(r); setViewOpen(true); };

  const save = async () => {
    // product_name may be empty for narcotic PDF imports; only generic_name + drug_class required
    if (!form.generic_name.trim() || !form.drug_class.trim()) {
      toast.error("Generic Name and Drug Class are required"); return;
    }
    // For manual create (not edit), product_name is still required
    if (!editing && !form.product_name.trim()) {
      toast.error("Product Name is required for manual entry"); return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/banned-products/${editing.id}`, form, { headers: getAuthHeader() });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/banned-products`, form, { headers: getAuthHeader() });
        toast.success("Product added to the Banned Drug List");
      }
      setEditOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (r) => {
    const label = r.product_name || r.generic_name || "this entry";
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/banned-products/${r.id}`, { headers: getAuthHeader() }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const downloadFile = async (path, filename) => {
    try {
      const r = await axios.get(`${API}${path}`, { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const openImport = () => { setImportPreview(null); setImportOpen(true); };
  const uploadFile = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await axios.post(`${API}/banned-products/import/preview`, fd,
        { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      setImportPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    if (fileRef.current) fileRef.current.value = "";
  };
  const confirmImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const r = await axios.post(`${API}/banned-products/import/confirm`,
        { new_rows: importPreview.new_rows }, { headers: getAuthHeader() });
      toast.success(`Imported ${r.data.inserted}, skipped ${r.data.skipped}`);
      setImportOpen(false); setImportPreview(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const printTable = () => window.print();

  return (
    <div className="p-6 space-y-4">
      {/* Warning banner */}
      <div className="rounded-lg bg-gradient-to-r from-red-500 via-red-600 to-orange-500 text-white p-5 shadow-md flex items-center gap-4" data-testid="banned-header-banner">
        <Ban className="w-10 h-10 text-white/95" />
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" data-testid="banned-header-title">
            🚫 BANNED DRUG LIST — NOT FOR SALE
          </h1>
          <p className="text-sm text-white/90 mt-1">Centralized restricted / prohibited / discontinued medicine registry. Referenced by every document workflow.</p>
        </div>
        <Badge className="bg-white/20 text-white border-white/30 text-sm">{totalAll} Products</Badge>
      </div>

      {/* Blocked Attempts Audit Trail */}
      <BlockedAttemptsCard />

      {/* Controls */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search product / generic / drug class…"
                     className="pl-9" data-testid="banned-search" />
            </div>
            {isAdmin && (
              <>
                <Button onClick={openCreate} data-testid="banned-add"><Plus className="w-4 h-4 mr-1" /> Add New Product</Button>
                <Button variant="outline" onClick={openImport} data-testid="banned-import">
                  <Upload className="w-4 h-4 mr-1" /> Import Excel / PDF
                </Button>
                <Button variant="outline" onClick={() => downloadFile("/banned-products/template", "banned_products_template.xlsx")} data-testid="banned-template">
                  <Download className="w-4 h-4 mr-1" /> Download Template
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => downloadFile("/banned-products/export.xlsx", "banned_products.xlsx")} data-testid="banned-export-xlsx">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button variant="outline" onClick={() => downloadFile("/banned-products/export.csv", "banned_products.csv")} data-testid="banned-export-csv">
              <FileText className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button variant="outline" onClick={() => downloadFile("/banned-products/export.pdf", "banned_products.pdf")} data-testid="banned-export-pdf">
              <FileText className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button variant="outline" onClick={printTable} data-testid="banned-print">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto max-h-[62vh] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-white">
                <TableRow className="bg-slate-100">
                  <TableHead className="w-14">S.No</TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => changeSort("product_name")} data-testid="banned-sort-product">
                    Product Name <SortIcon col="product_name" sortBy={sortBy} sortDir={sortDir} />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => changeSort("generic_name")} data-testid="banned-sort-generic">
                    Generic Name <SortIcon col="generic_name" sortBy={sortBy} sortDir={sortDir} />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => changeSort("drug_class")} data-testid="banned-sort-class">
                    Drug Class <SortIcon col="drug_class" sortBy={sortBy} sortDir={sortDir} />
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No banned products found.</TableCell></TableRow>
                ) : rows.map((r, i) => (
                  <TableRow key={r.id} data-testid={`banned-row-${r.id}`} className="hover:bg-red-50/40">
                    <TableCell className="text-xs text-slate-500">{(page - 1) * pageSize + i + 1}</TableCell>
                    <TableCell className="font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <BanIcon className="w-3.5 h-3.5 text-red-500 shrink-0" />
                        {r.product_name || <span className="text-slate-400 italic text-xs">—</span>}
                      </span>
                    </TableCell>
                    <TableCell>{r.generic_name}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{r.drug_class}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openView(r)} data-testid={`banned-view-${r.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`banned-edit-${r.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => del(r)} data-testid={`banned-delete-${r.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            <div className="text-xs text-slate-500">
              Showing {rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length} of {total}
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Rows per page:</Label>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-20 h-8" data-testid="banned-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>{PAGE_SIZE_OPTIONS.map(s => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)} data-testid="banned-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-slate-600">Page {page} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="banned-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="banned-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Banned Product" : "Add New Banned Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Product Name *</Label>
              <Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                     className="mt-1" placeholder="e.g. Ivermectin 12mg" data-testid="banned-form-product" />
            </div>
            <div>
              <Label className="text-xs">Generic Name *</Label>
              <Input value={form.generic_name} onChange={(e) => setForm({ ...form, generic_name: e.target.value })}
                     className="mt-1" placeholder="e.g. Ivermectin" data-testid="banned-form-generic" />
            </div>
            <div>
              <Label className="text-xs">Drug Class *</Label>
              <Input value={form.drug_class} onChange={(e) => setForm({ ...form, drug_class: e.target.value })}
                     className="mt-1" placeholder="e.g. Anti-parasitic" list="banned-class-suggestions" data-testid="banned-form-class" />
              <datalist id="banned-class-suggestions">
                {drugClasses.map(c => <option key={c} value={c} />)}
              </datalist>
              <div className="flex flex-wrap gap-1 mt-2">
                {drugClasses.slice(0, 10).map(c => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, drug_class: c })}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-indigo-100 hover:text-indigo-700">
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-red-600 hover:bg-red-700" data-testid="banned-form-save">
              {editing ? "Save Changes" : "Add to Banned List"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-md" data-testid="banned-view-dialog">
          <DialogHeader><DialogTitle>Banned Product Details</DialogTitle></DialogHeader>
          {viewRow && (
            <div className="space-y-3 text-sm">
              <Detail label="Product Name" value={viewRow.product_name} />
              <Detail label="Generic Name" value={viewRow.generic_name} />
              <Detail label="Drug Class" value={<Badge variant="secondary">{viewRow.drug_class}</Badge>} />
              <Detail label="Added On" value={(viewRow.created_at || "").slice(0, 10)} />
              <Detail label="Added By" value={viewRow.created_by || "—"} />
              <div className="pt-2 border-t border-slate-200">
                <div className="flex items-center gap-2 text-red-700 text-xs">
                  <AlertTriangle className="w-4 h-4" /> This product is blocked from all document workflows.
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="banned-import-dialog">
          <DialogHeader><DialogTitle>Import Banned Products (Excel / CSV / PDF)</DialogTitle></DialogHeader>
          {!importPreview ? (
            <div className="border-2 border-dashed border-red-200 bg-red-50/40 rounded-lg p-6 text-center">
              <Upload className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <div className="text-sm text-slate-700">Upload .xlsx / .xls / .csv / <b className="text-red-700">.pdf</b> (columns: Product Name, Generic Name, Drug Class — PDFs are auto-parsed)</div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden"
                     onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="banned-import-file" />
              <Button className="mt-3 bg-red-600 hover:bg-red-700" onClick={() => fileRef.current?.click()} data-testid="banned-import-choose">Choose File</Button>
              <div className="mt-2">
                <Button variant="link" onClick={() => downloadFile("/banned-products/template", "banned_products_template.xlsx")} className="text-xs">
                  Download Template
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2 text-center">
                <MiniStat val={importPreview.total_rows} label="Total Rows" tint="slate" />
                <MiniStat val={importPreview.new_rows.length} label="New" tint="emerald" />
                <MiniStat val={importPreview.duplicates_in_db.length} label="DB Duplicates" tint="amber" />
                <MiniStat val={importPreview.duplicates_in_file.length} label="File Duplicates" tint="rose" />
              </div>
              {importPreview.warnings?.length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded p-2 text-xs text-amber-800">
                  {importPreview.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                </div>
              )}
              <div className="border rounded max-h-[300px] overflow-y-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead>Product</TableHead><TableHead>Generic</TableHead>
                    <TableHead>Class</TableHead><TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {importPreview.new_rows.map((r, i) => (
                      <TableRow key={`n${i}`}>
                        <TableCell className="text-sm">{r.product_name || <span className="text-slate-400 italic">—</span>}</TableCell>
                        <TableCell className="text-sm">{r.generic_name}</TableCell>
                        <TableCell className="text-sm">{r.drug_class}</TableCell>
                        <TableCell><Badge className="text-[10px] bg-emerald-100 text-emerald-700">NEW</Badge></TableCell>
                      </TableRow>
                    ))}
                    {importPreview.duplicates_in_db.map((r, i) => (
                      <TableRow key={`d${i}`} className="opacity-60">
                        <TableCell className="text-sm">{r.product_name || <span className="text-slate-400 italic">—</span>}</TableCell>
                        <TableCell className="text-sm">{r.generic_name}</TableCell>
                        <TableCell className="text-sm">{r.drug_class}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">SKIP (DB dup)</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setImportPreview(null)}>Back</Button>
                <Button className="bg-red-600 hover:bg-red-700" onClick={confirmImport} disabled={importing || importPreview.new_rows.length === 0} data-testid="banned-import-confirm">
                  {importing ? "Importing…" : `Import ${importPreview.new_rows.length} New Products`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div className="flex justify-between border-b pb-1">
    <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
    <span className="text-sm font-medium text-slate-800">{value}</span>
  </div>
);

const MiniStat = ({ val, label, tint }) => (
  <div className={`rounded p-3 bg-${tint}-50 text-${tint}-800`}>
    <div className="text-2xl font-bold">{val}</div>
    <div className="text-[10px] uppercase tracking-wide">{label}</div>
  </div>
);

export default ProcessingTeamPanel;

/* ---------------------- Blocked Attempts Audit Trail ---------------------- */

const RANGE_OPTIONS = [
  { label: "24 hours", days: 1 },
  { label: "7 days",   days: 7 },
  { label: "30 days",  days: 30 },
  { label: "90 days",  days: 90 },
];

const BlockedAttemptsCard = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/banned-products/blocked-attempts?days=${days}&limit=50`,
        { headers: getAuthHeader() });
      setData(r.data);
    } catch (e) {
      // silent — user may not have permission
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return null; // hide until first load
  }

  const total = data.total_attempts || 0;
  const uniq = data.unique_banned_products || 0;
  const summary = data.summary || [];
  const currentLabel = RANGE_OPTIONS.find((r) => r.days === days)?.label || `${days} days`;

  // If there are zero attempts, show a subtle compact card
  if (total === 0) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="blocked-attempts-empty">
        <CardContent className="py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Activity className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-emerald-900">
              No banned-product upload attempts in the last {currentLabel}
            </div>
            <div className="text-xs text-emerald-700">Your team&apos;s document generation is clean. </div>
          </div>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-8 w-32" data-testid="blocked-attempts-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((r) => (
                <SelectItem key={r.days} value={String(r.days)}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-sm"
          data-testid="blocked-attempts-card">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <ShieldOff className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2 text-amber-900">
                Blocked Upload Attempts
                <Badge className="bg-amber-600 text-white text-[10px]" data-testid="blocked-attempts-count">
                  {total}
                </Badge>
              </CardTitle>
              <div className="text-xs text-amber-800 mt-0.5">
                <b>{total}</b> document generation attempt{total !== 1 ? "s" : ""} blocked in the last {currentLabel} ·{" "}
                <b>{uniq}</b> unique banned product{uniq !== 1 ? "s" : ""} triggered warnings.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="h-8 w-32" data-testid="blocked-attempts-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map((r) => (
                  <SelectItem key={r.days} value={String(r.days)}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => setExpanded((e) => !e)}
                    data-testid="blocked-attempts-toggle" className="border-amber-300">
              {expanded ? "Hide details" : "View details"}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Top offenders (always visible) */}
        <div className="flex flex-wrap gap-2 mt-1">
          {summary.slice(0, 6).map((s, i) => (
            <span key={i}
                  className="inline-flex items-center gap-1.5 text-xs bg-white border border-amber-200 rounded-full px-2.5 py-1 shadow-sm"
                  data-testid={`blocked-summary-${i}`}>
              <span className="font-bold text-amber-700">{s.attempts}×</span>
              <span className="font-semibold text-slate-800">{s.product_name || "—"}</span>
              {s.generic_name && (
                <span className="text-slate-500">({s.generic_name})</span>
              )}
              {s.drug_class && (
                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900">
                  {s.drug_class}
                </span>
              )}
            </span>
          ))}
          {summary.length > 6 && (
            <span className="text-xs text-amber-700 italic self-center">
              +{summary.length - 6} more
            </span>
          )}
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="mt-4 space-y-4">
            {/* Full summary table */}
            <div className="border border-amber-200 rounded-md bg-white overflow-x-auto">
              <Table>
                <TableHeader className="bg-amber-50">
                  <TableRow>
                    <TableHead className="text-xs">Banned Product</TableHead>
                    <TableHead className="text-xs">Generic Name</TableHead>
                    <TableHead className="text-xs">Drug Class</TableHead>
                    <TableHead className="text-xs text-right">Attempts</TableHead>
                    <TableHead className="text-xs">Last Attempted</TableHead>
                    <TableHead className="text-xs">By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.map((s, i) => (
                    <TableRow key={i} data-testid={`blocked-summary-row-${i}`}>
                      <TableCell className="text-sm font-semibold text-red-800">{s.product_name || "—"}</TableCell>
                      <TableCell className="text-sm">{s.generic_name || "—"}</TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline" className="text-[10px]">{s.drug_class || "—"}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-right font-bold text-amber-700">{s.attempts}</TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {s.last_attempted_at ? new Date(s.last_attempted_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{s.last_attempted_by || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Recent raw log */}
            {(data.recent || []).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5" /> Recent attempts
                </h4>
                <div className="border rounded-md bg-white max-h-72 overflow-y-auto">
                  <Table>
                    <TableHeader className="bg-slate-50 sticky top-0">
                      <TableRow>
                        <TableHead className="text-xs">When</TableHead>
                        <TableHead className="text-xs">Document</TableHead>
                        <TableHead className="text-xs">User</TableHead>
                        <TableHead className="text-xs">Banned Products</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.recent.map((r, i) => (
                        <TableRow key={i} data-testid={`blocked-recent-row-${i}`}>
                          <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                            {r.attempted_at ? new Date(r.attempted_at).toLocaleString() : "—"}
                          </TableCell>
                          <TableCell className="text-xs">
                            <Badge variant="outline" className="text-[10px]">{r.document_type || "—"}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-700">
                            {r.attempted_by_name || r.attempted_by || "—"}
                          </TableCell>
                          <TableCell className="text-xs">
                            <div className="flex flex-wrap gap-1">
                              {(r.banned_products || []).map((bp, j) => (
                                <span key={j} className="inline-block bg-red-50 text-red-800 border border-red-200 rounded px-1.5 py-0.5">
                                  {bp.product_name} <span className="text-red-500">({bp.generic_name})</span>
                                </span>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="text-xs text-slate-400 mt-2">Refreshing…</div>
        )}
      </CardContent>
    </Card>
  );
};
